import { useTabStore } from '~/stores/tab'
import type { Tab } from '~/stores/tab'
import { useMenuStore } from '~/stores/menu'
import type { MenuResponse } from '~/types/menu'
import { HOME_TAB_ID, HOME_TAB_PATH, HOME_TAB_TITLE } from '~/constants'

function findMenuByPath(menus: MenuResponse[], path: string): MenuResponse | null {
  for (const menu of menus) {
    if (menu.path === path) return menu
    if (menu.children?.length) {
      const found = findMenuByPath(menu.children, path)
      if (found) return found
    }
  }
  return null
}

/**
 * 현재 경로의 가장 긴 prefix를 가진 부모 메뉴 항목을 반환한다.
 * 예: '/base/demo/board/1' → MenuResponse{ path: '/base/demo/board' }
 */
function findParentMenu(menus: MenuResponse[], path: string): MenuResponse | null {
  const candidates: MenuResponse[] = []
  const collect = (items: MenuResponse[]) => {
    for (const m of items) {
      if (m.path && path.startsWith(m.path + '/')) candidates.push(m)
      if (m.children?.length) collect(m.children)
    }
  }
  collect(menus)
  // 가장 긴 경로(=가장 구체적인 부모) 우선
  return candidates.sort((a, b) => (b.path?.length ?? 0) - (a.path?.length ?? 0))[0] ?? null
}

export default defineNuxtRouteMiddleware(async (to) => {
  if (process.server) return

  // 관리자 영역 외 경로 또는 에러 페이지는 탭 처리에서 제외
  if (!to.path.startsWith('/base') && !to.path.startsWith('/biz')) return
  if (to.path.startsWith('/base/error/')) return

  const tabStore  = useTabStore()
  const menuStore = useMenuStore()

  // 이미 열려 있는 탭이면 활성화만 하고 통과 (메뉴 fetch 불필요)
  const existing = tabStore.tabs.find(t => t.id === to.path)
  if (existing) {
    tabStore.setActiveTab(to.path)
    return
  }

  // 새 탭 생성 전: 메뉴 데이터가 로드되지 않았다면 대기
  // (fetchMenus 는 loaded 플래그로 중복 호출을 막고, 내부에서 토큰을 먼저 확보한다)
  await menuStore.fetchMenus()

  const isHome = to.path === HOME_TAB_ID || to.path === HOME_TAB_PATH + '/'

  let title : string             = HOME_TAB_TITLE
  let msgKey: string | undefined = isHome ? 'PAGE_KSIS_MAIN' : undefined

  if (!isHome) {
    const menu = findMenuByPath(menuStore.menus, to.path)

    if (!menu) {
      // 메뉴에 직접 등록되지 않은 하위 경로(상세·등록·수정 등):
      // 부모 메뉴 탭을 찾아 해당 탭 안에서 이동하고 새 탭을 만들지 않는다.
      const parentMenu = findParentMenu(menuStore.menus, to.path)
      if (parentMenu?.path) {
        const parentTab: Tab | undefined = tabStore.tabs.find(t => t.id === parentMenu.path)
        if (parentTab) {
          // 이미 열린 부모 탭의 현재 경로를 갱신하고 활성화
          parentTab.path = to.path
          tabStore.setActiveTab(parentTab.id)
        } else {
          // 부모 탭이 아직 없으면 생성하면서 실제 경로는 현재 하위 경로로 설정
          const canOpen = tabStore.addTab({
            id:       parentMenu.path,
            title:    parentMenu.name   ?? deriveTitle(parentMenu.path),
            path:     to.path,
            closable: true,
            msgKey:   parentMenu.msgKey ?? undefined,
          })
          if (!canOpen) return abortNavigation()
        }
      }
      return
    }

    title  = menu.name    ?? deriveTitle(to.path)
    // null → undefined 변환: Tab 인터페이스가 string | undefined 를 기대함
    msgKey = menu.msgKey  ?? undefined
  }

  const canOpen = tabStore.addTab({
    id:       to.path,
    title,
    path:     to.path,
    closable: !isHome,
    msgKey,
  })

  if (!canOpen) {
    return abortNavigation()
  }
})

/** 경로 세그먼트에서 표시 제목을 유추한다 (메뉴 데이터가 없을 때 폴백) */
function deriveTitle(path: string): string {
  const segments = path.replace(/^\/(base|biz)\/?/, '').split('/')
  const last = segments.filter(Boolean).pop() ?? '페이지'
  return last
    .replace(/-/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
}
